
import { useState } from 'react';
import { Trash, Tag, Loader2, Check, X, SquareCheck, MousePointerSquareDashed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Product } from './ProductCard';

interface BulkOperationsProps {
  selectedProducts: Product[];
  onUpdateCategory: (products: Product[], newCategory: string) => Promise<boolean>;
  onDeleteProducts: (products: Product[]) => Promise<boolean>;
  onClearSelection: () => void;
  onSelectAll?: () => void;
  onTagAll?: (products: Product[], newCategory: string) => Promise<boolean>;
  availableProducts?: Product[];
}

export function BulkOperations({
  selectedProducts,
  onUpdateCategory,
  onDeleteProducts,
  onClearSelection,
  onSelectAll,
  onTagAll,
  availableProducts = []
}: BulkOperationsProps) {
  const [newCategory, setNewCategory] = useState('');
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTaggingAll, setIsTaggingAll] = useState(false);

  const handleUpdateCategory = async () => {
    if (!newCategory.trim()) return;
    
    setIsUpdatingCategory(true);
    try {
      await onUpdateCategory(selectedProducts, newCategory);
      setNewCategory('');
    } finally {
      setIsUpdatingCategory(false);
    }
  };

  const handleTagAll = async () => {
    if (!newCategory.trim() || !onTagAll || availableProducts.length === 0) return;
    
    setIsTaggingAll(true);
    try {
      await onTagAll(availableProducts, newCategory);
      setNewCategory('');
    } finally {
      setIsTaggingAll(false);
    }
  };

  const handleDeleteSelected = async () => {
    setIsDeleting(true);
    try {
      await onDeleteProducts(selectedProducts);
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  // Group products by source
  const productCount = selectedProducts.length;
  const productsCount = selectedProducts.filter(p => p.source === 'products').length;
  const recommendationsCount = selectedProducts.filter(p => p.source === 'recommendations').length;

  // Calculate how many products are available to select
  const allProductsSelected = availableProducts.length > 0 && 
                             availableProducts.length === selectedProducts.length;
  const canSelectMore = availableProducts.length > selectedProducts.length;

  if (productCount === 0) {
    return null;
  }

  return (
    <Card className="mb-4 border-primary bg-primary/5">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center">
            <SquareCheck className="h-5 w-5 mr-2 text-primary" />
            <div>
              <h3 className="font-medium text-primary">
                {productCount} product{productCount !== 1 ? 's' : ''} selected
              </h3>
              <p className="text-sm text-muted-foreground">
                {productsCount > 0 && `${productsCount} permanent product${productsCount !== 1 ? 's' : ''}`}
                {productsCount > 0 && recommendationsCount > 0 && ', '}
                {recommendationsCount > 0 && `${recommendationsCount} recommendation${recommendationsCount !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <div className="flex gap-2 items-center flex-1 sm:flex-auto">
              <Input
                placeholder="New category..."
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="w-full max-w-xs"
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleUpdateCategory}
                disabled={!newCategory.trim() || isUpdatingCategory}
              >
                {isUpdatingCategory ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Tag className="h-4 w-4 mr-1" />
                )}
                Update Category
              </Button>
              
              {onTagAll && availableProducts.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleTagAll}
                  disabled={!newCategory.trim() || isTaggingAll}
                >
                  {isTaggingAll ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Tag className="h-4 w-4 mr-1" />
                  )}
                  Tag All
                </Button>
              )}
            </div>
            
            {onSelectAll && canSelectMore && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onSelectAll}
                title={allProductsSelected ? "All products selected" : "Select all visible products"}
                disabled={allProductsSelected}
              >
                <MousePointerSquareDashed className="h-4 w-4 mr-1" />
                Select All
              </Button>
            )}
            
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              <Trash className="h-4 w-4 mr-1" />
              Delete Selected
            </Button>
            
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={onClearSelection}
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>
        </div>
      </CardContent>
      
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete {productCount} selected product{productCount !== 1 ? 's' : ''}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)} disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSelected} 
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
